import { AnimatePresence, motion, useAnimationFrame } from "motion/react";
import PropTypes from "prop-types";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import styled from "styled-components/macro";
import { padding } from "theme";

import isEqual from "lodash/isEqual";

import FnrErrorBoundary from "UI/FnrErrorBoundary";
import {
  scrollbarDarkThin3,
  scrollbarDarkWide,
  scrollbarInvisibleThin,
} from "UI/scrollbarStyles";
import { Z_POPOVER } from "UI/zIndex";

import useComponentIsMounted from "Utilities/Hooks/useComponentIsMounted";
import useEscapeKey from "Utilities/Hooks/useEscapeKey";
import useOnClickOutside from "Utilities/Hooks/useOnClickOutside";
import Portal from "Utilities/PortalMaker";

import { FnrPopoverContext } from "./Wrapper";

export const FnrPopoverStateContext = createContext();

FnrPopover.propTypes = {
  children: PropTypes.node.isRequired,
  show: PropTypes.bool,
  onShouldClose: PropTypes.func,
  /** Ref of the button that toggles the popover */
  buttonRef: PropTypes.object,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  onAnimationComplete: PropTypes.func,
  style: PropTypes.object,
  offsetLeft: PropTypes.number,
  closeTrigger: PropTypes.any,
  color: PropTypes.oneOf(["blue", "red", "white"]),
  variant: PropTypes.oneOf(["popover", "container"]),
  thinBorder: PropTypes.bool,
  id: PropTypes.string,
  /** Semantic name for this popover instance, rendered as data-popover-name on the container */
  popoverName: PropTypes.string,
  /** an element data tag to ignore */
  dataElementToIgnore: PropTypes.string,
  /** ignore all click event inside a class or id. ex: ".parentClass", "#parentId" */
  targetContainerToIgnore: PropTypes.string,
  dataCancelClickAway: PropTypes.string,
  /** Optional delay for the animation in */
  delay: PropTypes.number,
  /** Adds additional padding to wrapper */
  addPadding: PropTypes.bool,
  scrollbarStyle: PropTypes.oneOf(["dark", "darkWide", "invisible"]),
  autoFocus: PropTypes.bool,
};

export function FnrPopover({
  children,
  show: showArg,
  onShouldClose,
  buttonRef: buttonRefArg,
  onOpen,
  onClose,
  onAnimationComplete,
  style: styleArg = {},
  offsetLeft = 0,
  closeTrigger = 0,
  color = "blue", // options: "blue", "red", "white"
  variant = "popover",
  thinBorder,
  id,
  popoverName,
  dataElementToIgnore,
  targetContainerToIgnore,
  dataCancelClickAway,
  delay = 0,
  scrollbarStyle = "invisible",
  autoFocus,
}) {
  const context = useContext(FnrPopoverContext);

  const [controlled] = useState(!context);

  const dispatch = useMemo(() => {
    if (!controlled) return context[1];
  }, [context, controlled]);

  const show = useMemo(() => {
    if (!controlled) return context[0].show;
    else return showArg;
  }, [context, controlled, showArg]);

  const buttonRef = useMemo(() => {
    if (!controlled) return context[0].buttonRef;
    else return buttonRefArg;
  }, [buttonRefArg, context, controlled]);

  const localPopoverRef = useRef();
  const popoverRef = useMemo(() => {
    if (!controlled) return context[0].popoverRef;
    return localPopoverRef;
  }, [context, controlled]);

  const componentIsMounted = useComponentIsMounted();
  const [position, setPosition] = useState(false);
  const recentlyIgnoredRef = useRef(false);

  const shouldIgnoreTarget = useCallback(
    (e) => {
      const ignoreFromDataElement =
        e &&
        dataElementToIgnore &&
        e?.target.closest(`[data-element="${dataElementToIgnore}"]`);
      const ignoreFromTargetContainer =
        e && e?.target.closest(targetContainerToIgnore);
      return ignoreFromDataElement || ignoreFromTargetContainer;
    },
    [dataElementToIgnore, targetContainerToIgnore]
  );

  const attemptClosePopover = useCallback(
    (e) => {
      const ignoreTarget = shouldIgnoreTarget(e);

      if (ignoreTarget) {
        // Suppress subsequent events (e.g. focusin after mousedown) from the
        // same user interaction so a nested-popover unmount doesn't
        // re-trigger close on the parent.
        recentlyIgnoredRef.current = true;
        setTimeout(() => {
          recentlyIgnoredRef.current = false;
        }, 100);
        return;
      }

      if (recentlyIgnoredRef.current) return;

      if (!controlled) dispatch({ type: "Hide Popover" });
      else if (onShouldClose) {
        onShouldClose(e);
      }
    },
    [controlled, dispatch, onShouldClose, shouldIgnoreTarget]
  );

  useOnClickOutside(popoverRef, (e) => {
    if (!buttonRef?.current?.contains(e.target)) attemptClosePopover(e);
  });

  const handleEscape = useCallback(() => {
    attemptClosePopover();
    buttonRef.current?.focus();
  }, [attemptClosePopover, buttonRef]);

  useEscapeKey(handleEscape, show);

  const [oldCloseTrigger, setOldCloseTrigger] = useState(closeTrigger);
  useEffect(() => {
    if (closeTrigger !== oldCloseTrigger) {
      attemptClosePopover();
      setOldCloseTrigger(closeTrigger);
    }
  }, [attemptClosePopover, closeTrigger, oldCloseTrigger]);

  // Fire onOpen/onClose once per open↔closed transition, not on every effect
  // run. `onOpen`/`onClose` are in this effect's deps, and consumers routinely
  // pass inline (fresh-identity-per-render) handlers — e.g. PencilEditInput's
  // More Menu: `onOpen={() => { onEdit(); ... }}`. If such an onOpen updates
  // state, that re-render mints a new onOpen identity, re-runs this effect, and
  // re-invoking onOpen here would loop to "Maximum update depth exceeded".
  // Gating on the transition makes the effect idempotent across re-renders
  // while `show` holds steady. `setPosition(false)` still runs on every
  // not-shown pass (it self-bails when already false).
  const wasShownRef = useRef(false);
  useEffect(() => {
    if (!componentIsMounted) return;
    if (!buttonRef?.current) return;

    if (!show) {
      setPosition(false);

      if (wasShownRef.current) {
        wasShownRef.current = false;
        if (onClose) onClose();
      }

      return;
    }

    if (!wasShownRef.current) {
      wasShownRef.current = true;
      if (onOpen) onOpen();
    }
  }, [buttonRef, componentIsMounted, offsetLeft, onClose, onOpen, show]);

  useAnimationFrame(() => {
    if (show) {
      // Use offsetWidth/offsetHeight for sizing — these are untransformed layout
      // dimensions, unaffected by the scale animation. getBoundingClientRect()
      // returns the visually transformed size, which changes mid-animation and
      // can cause placement decisions to flip (e.g. below → above) during the
      // entrance animation.
      const popoverEl = popoverRef?.current;
      const contentWidth = popoverEl?.offsetWidth || 0;
      const contentHeight = popoverEl?.scrollHeight || 0;

      const parentPosition = buttonRef?.current?.getBoundingClientRect() || {};

      // getting a nice default to start with
      const newPosition = {
        top: (parentPosition.bottom || 0) + 4,
        left: (parentPosition.left || 0) - 4 + offsetLeft,
        originX: 0,
        originY: 0,
      };

      if (contentWidth + newPosition.left > window.innerWidth - 20) {
        delete newPosition.left;
        newPosition.right = 20;
      }

      if (
        (newPosition.left || newPosition.right) + contentWidth >=
        window.innerWidth - 20
      )
        newPosition.maxWidth = window.innerWidth - 40;

      const parentMiddleX = parentPosition.left + parentPosition.width / 2;
      const popoverWidth = newPosition.maxWidth || contentWidth;
      const overlapX =
        parentMiddleX -
        (newPosition.left ||
          window.innerWidth - newPosition.right - popoverWidth);
      newPosition.originX = overlapX / popoverWidth;

      const parentMiddleY = parentPosition.top + parentPosition.height / 2;

      if (contentHeight > window.innerHeight - 40) {
        newPosition.top = 20;
        newPosition.maxHeight = window.innerHeight - 40;
      } else if (newPosition.top + contentHeight > window.innerHeight - 20) {
        if (parentPosition.top - 4 - contentHeight > 20) {
          delete newPosition.top;
          newPosition.bottom = window.innerHeight - (parentPosition.top - 4);
        } else {
          let newTop = parentMiddleY - contentHeight / 2;

          const buttonIsInTopHalfOfWindow =
            parentMiddleY < window.innerHeight / 2;

          if (
            !buttonIsInTopHalfOfWindow &&
            newTop + contentHeight > window.innerHeight - 20
          ) {
            newTop = window.innerHeight - 20 - contentHeight;
          } else if (buttonIsInTopHalfOfWindow && newTop < 20) newTop = 20;

          newPosition.top = newTop;
        }
      }

      if (!newPosition.top) newPosition.originY = 1;
      else if (newPosition.top >= parentPosition.bottom)
        newPosition.originY = 0;
      else {
        const popoverHeight = newPosition.maxHeight || contentHeight;
        const overlapY = parentMiddleY - newPosition.top;
        newPosition.originY = overlapY / popoverHeight;
      }
      if (!isEqual(newPosition, position)) {
        setPosition(newPosition);
      }
    }
  });

  const popoverState = {
    show,
    onShouldClose,
  };

  if (!controlled && !onShouldClose)
    popoverState.onShouldClose = () => dispatch({ type: "Hide Popover" });

  const style = useMemo(() => {
    if (position) return { ...styleArg, ...position };
    /* Placement is measured off the mounted popover, so the first commit has
       no coordinates to give it yet and the frame loop only answers a frame or
       so later. Left at the portal's own corner for that frame it is drawn top
       left of the screen, and the entrance animation — which runs on elapsed
       time, not on renders — can be well up from opacity 0 by the time the
       real spot lands. Parked off-screen it measures the same and focuses the
       same, and there is nothing to catch. */
    return { ...styleArg, top: -9999, left: -9999 };
  }, [position, styleArg]);

  return (
    <Portal to={document.getElementById("FnrOverlayHolder")}>
      <AnimatePresence>
        {show && (
          <PopChildRefWrapper
            data-cancel-click-away={dataCancelClickAway}
            tabIndex={-1}
            innerRef={(node) => {
              if (typeof node?.focus === "function" && autoFocus) node.focus();
              popoverRef.current = node;
            }}
            onBlur={(e) => {
              const nextFocus = e.relatedTarget;
              if (nextFocus && popoverRef.current?.contains(nextFocus)) {
                // still inside popover → do nothing
                return;
              }
              // Nested popovers (FnrSelectList2 country/state pickers, date
              // pickers, etc.) render into a portal as DOM siblings of this
              // popover and tag their root with `data-cancel-click-away` —
              // the same contract `shouldIgnoreTarget` honors above. Focus
              // moving into one of those means the user is still
              // interacting with our chrome; don't yank focus back to the
              // anchor (which would close the nested popover via its own
              // click-outside detector). Modals/overlays don't set this
              // attribute, so the outer popover still yields focus when
              // one opens over it.
              if (
                nextFocus &&
                typeof nextFocus.closest === "function" &&
                nextFocus.closest("[data-cancel-click-away]")
              ) {
                return;
              }
              // otherwise, focus left popover
              buttonRef.current?.focus();
            }}
            $scrollbarStyle={scrollbarStyle}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{
              scale: 1,
              opacity: 1,
              transition: {
                delay: delay,
              },
            }}
            exit={{
              scale: 0.8,
              opacity: 0,
              transition: {
                type: "tween",
                duration: 0.3,
                ease: "backIn",
              },
            }}
            onAnimationComplete={onAnimationComplete}
            style={style}
            color={color}
            $variant={variant}
            $thinBorder={thinBorder}
            id={id}
            data-popover-name={popoverName}
            data-cy="FnrPopover_Container"
          >
            <FnrErrorBoundary boundaryName="FnrPopover">
              <FnrPopoverStateContext.Provider value={popoverState}>
                {children}
              </FnrPopoverStateContext.Provider>
            </FnrErrorBoundary>
          </PopChildRefWrapper>
        )}
      </AnimatePresence>
    </Portal>
  );
}

/**
 * Avoids passing `ref` as a JSX ref prop to the direct child of
 * AnimatePresence. Framer-motion's PopChild accesses `children.props.ref`
 * and `children.ref` internally, which triggers React 18's
 * "ref is not a prop" warning (thrown as an error in tests via setupTests.js).
 *
 * By accepting the ref as `innerRef` and forwarding it to Wrapper's `ref`,
 * no ref prop exists on the PopChildRefWrapper element itself, so React 18
 * never installs the warning getter.
 */
function PopChildRefWrapper({ innerRef, ...props }) {
  return <Wrapper ref={innerRef} {...props} />;
}

PopChildRefWrapper.propTypes = {
  innerRef: PropTypes.func,
};

const scrollbarStyles = {
  dark: scrollbarDarkThin3,
  darkWide: scrollbarDarkWide,
  invisible: scrollbarInvisibleThin,
};

const Wrapper = styled(motion.div)`
  ${({ $scrollbarStyle }) =>
    scrollbarStyles[$scrollbarStyle] || scrollbarInvisibleThin}
  overflow: auto;
  position: fixed;
  box-sizing: border-box;
  z-index: ${Z_POPOVER};
  min-width: 160px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  color: #fff;
  font-weight: 400;
  ${padding}

  /* autoFocus lands focus here so the keyboard carries on inside the popover,
     and tabIndex -1 means nobody can land on it any other way. The ring it
     draws is around the whole popover, which is the one thing on screen that
     already announced itself. */
  &:focus,
  &:focus-visible {
    outline: none;
  }

  /* FIREFOX (and anywhere backdrop-filter isn't supported) */
  background-image: linear-gradient(to right bottom, #384a5d, #4c6279f2);
  background-image: ${({ color }) =>
    color === "red"
      ? "linear-gradient(to right bottom, rgba(193, 39, 39, 0.9), rgba(136, 3, 3, 0.9));"
      : null};
  background: ${({ color }) =>
    color === "white" ? "rgba(236, 239, 240, 0.9)" : null};

  /* CHROME (and anywhere backdrop-filter is supported) */
  @supports (-webkit-backdrop-filter: none) or (backdrop-filter: none) {
    background-image: linear-gradient(
      to right bottom,
      rgba(19, 66, 107, 0.6),
      rgba(47, 63, 79, 0.6)
    );
    background-image: ${({ color }) =>
      color === "red"
        ? "linear-gradient( to right bottom, rgba(180, 37, 45, 0.7), rgba(128, 36, 40, 0.7) )"
        : null};
    background: ${({ color }) =>
      color === "white" ? "rgba(236, 239, 240,.5)" : null};
    backdrop-filter: blur(12px) saturate(2);
    -webkit-backdrop-filter: blur(12px) saturate(2);
  }
`;
